/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * TranscriptPanel.tsx — Transcrip\1 \2anel (services/operator-web-app).
 */

import { useEffect, useRef, useState } from 'react';
import { useCopilot } from '@/store';
import { Button } from '@/components/ui';
import type { TranscriptEntry } from '@/store/slices/copilotSlice';
import { useTranslation } from '@/i18n';

const SPEAKER_SHORT: Record<TranscriptEntry['speaker'], string> = {
  client: 'C',
  operator: 'O',
  bot: 'B',
};

/** E6-S1 T3: Initial window size for long calls (virtualization). */
const INITIAL_VISIBLE = 80;
const LOAD_MORE_STEP = 50;

/** E6-S1: Live transcript panel — partial/final stream, speaker labels, auto-scroll. E6-S1 T3: display window for long calls. */
export function TranscriptPanel() {
  const t = useTranslation();
  const copilot = useCopilot();
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const { transcript, partialText, partialSpeaker } = copilot;

  const total = transcript.length;
  const visibleEntries = total <= visibleCount ? transcript : transcript.slice(-visibleCount);
  const hasOlder = total > visibleCount;
  const olderCount = total - visibleCount;

  useEffect(() => {
    if (total === 0) setVisibleCount(INITIAL_VISIBLE);
  }, [total]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript.length, partialText]);

  const hasContent = total > 0 || partialText;

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_STEP, total));
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col min-h-0 max-h-[320px] overflow-hidden rounded-lg border"
      style={{ borderColor: 'var(--glass-border)', background: 'var(--toggle-bg)' }}
      aria-label={t('transcript.label')}
    >
      <div
        className="flex-shrink-0 px-3 py-2 border-b"
        style={{ borderColor: 'var(--glass-border)' }}
      >
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {t('transcript.title')}
        </h3>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-2 text-sm">
        {!hasContent && <p style={{ color: 'var(--text-muted)' }}>{t('transcript.waiting')}</p>}
        {hasOlder && (
          <div className="flex justify-center py-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadMore}
              aria-label={t('transcript.show_previous_label', { count: olderCount })}
            >
              {t('transcript.show_previous', { count: olderCount })}
            </Button>
          </div>
        )}
        {visibleEntries.map((entry, index) => {
          const globalIndex = total - visibleEntries.length + index;
          return <TranscriptLine key={`t-${globalIndex}-${entry.resultId}`} entry={entry} />;
        })}
        {partialText && partialSpeaker && (
          <p
            className="italic opacity-90"
            style={{ color: 'var(--text-secondary)' }}
            aria-live="polite"
            aria-label={t('transcript.draft', {
              speaker: t(`transcript.speaker.${partialSpeaker}`),
              text: partialText,
            })}
          >
            <span className="font-medium not-italic">{SPEAKER_SHORT[partialSpeaker]}:</span>{' '}
            {partialText}
          </p>
        )}
        <div ref={bottomRef} className="h-0" aria-hidden />
      </div>
    </div>
  );
}

function TranscriptLine({ entry }: { entry: TranscriptEntry }) {
  const t = useTranslation();
  const label = t(`transcript.speaker.${entry.speaker}`);
  const short = SPEAKER_SHORT[entry.speaker];
  return (
    <p
      style={{ color: 'var(--text-secondary)' }}
      data-speaker={entry.speaker}
      data-result-id={entry.resultId}
      aria-label={`${label}: ${entry.text}`}
    >
      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
        {short}:
      </span>{' '}
      {entry.text}
      {entry.confidence != null && (
        <span className="ml-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          ({Math.round(entry.confidence * 100)}%)
        </span>
      )}
    </p>
  );
}
